import { stageLabel } from '@/utils/labels';

import type { Channel, TriggerEvent, TriggerParams } from './api-types';

/** The three outbound channels (OUT-1), in display order, with what each one reaches today. */
export const CHANNELS: { id: Channel; label: string; note: string }[] = [
  { id: 'email', label: 'Email', note: 'Every lead has an email (OUT-5). No provider is chosen yet, so emails are only logged.' },
  { id: 'telegram', label: 'Telegram', note: 'Reaches only people in a group with the bot (OUT-6).' },
  {
    id: 'popup',
    label: 'Owner Hub notification',
    note: 'In the owner’s Owner Studio: the bell rings when it arrives, and the owner opens it from there. Only leads with an Owner Hub account get it, and you see when they saw and read it.',
  },
];

export const channelName = (c: Channel) => CHANNELS.find((x) => x.id === c)?.label ?? c;

/** Channels in a sentence: "email, Telegram and an Owner Hub notification". */
export const channelPhrase = (channels: Channel[]) => {
  const words = channels.map((c) => (c === 'email' ? 'email' : c === 'telegram' ? 'Telegram' : 'an Owner Hub notification'));
  return words.length > 1 ? `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}` : (words[0] ?? '');
};

/** Trigger events. The real list is still to be defined (OUT-3, Q14); only the first two fire for now (`off`: not in this version). */
export const TRIGGER_EVENTS: { id: TriggerEvent; label: string; hint: string; off?: boolean }[] = [
  { id: 'casino_launched', label: 'A casino goes live', hint: 'Seen by the Owner Hub sync (CAS-2).' },
  { id: 'lead_added', label: 'A lead is added', hint: 'Registered, wrote to us, or joined a Telegram group (LEAD-1, LEAD-2, LEAD-6).' },
  { id: 'no_players', label: 'No players for some days', hint: 'Stored, not evaluated yet (Q14).', off: true },
  { id: 'first_message', label: 'A lead writes for the first time', hint: 'On Telegram or by email (LEAD-6). Stored, not evaluated yet (Q14).', off: true },
  { id: 'stage_changed', label: 'A lead reaches a stage', hint: 'New, Configuring, Paid and Live change from platform events (CAS-4); Clarifications is set by a person. Stored, not evaluated yet (Q14).', off: true },
];

export const eventLabel = (e: TriggerEvent) => TRIGGER_EVENTS.find((x) => x.id === e)?.label ?? e;

/** "A casino goes live", "No players for 30 days", "Stage becomes Live". */
export const describeEvent = (t: { event: TriggerEvent; params: TriggerParams }) => {
  if (t.event === 'no_players') return `No players for ${t.params.days ?? 30} days`;
  if (t.event === 'stage_changed') return t.params.stage ? `Stage becomes ${stageLabel(t.params.stage)}` : 'Stage changes';
  return eventLabel(t.event);
};

export const describeDelay = (hours: number) => {
  if (hours === 0) return 'Right away';
  if (hours % 24 === 0) return `After ${hours / 24} ${hours === 24 ? 'day' : 'days'}`;
  return `After ${hours} ${hours === 1 ? 'hour' : 'hours'}`;
};

/** Variables a message or template can use, with the sample values the previews show. */
export const VARIABLES: { key: string; label: string; sample: string }[] = [
  { key: 'first_name', label: 'First name', sample: 'Maria' },
  { key: 'casino_name', label: 'Casino name', sample: 'Royal Fortune' },
  { key: 'dashboard_link', label: 'Dashboard link', sample: 'your Owner Hub dashboard' },
  { key: 'team_member', label: 'Team member', sample: 'Sara Ionescu' },
];

/** The text with every known {{variable}} replaced by its sample value. */
export const fillSample = (text: string) => text.replace(/\{\{\s*(\w+)\s*\}\}/g, (all, key: string) => VARIABLES.find((v) => v.key === key)?.sample ?? all);
